import { useState } from 'react';
import {
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  type TooltipProps,
  ComposedChart
} from 'recharts';
import { ChartContainer, type ChartConfig } from '../../../../components/ui/Chart';

// ----------------------------------------------------------------------

const CHART_COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--chart-2, 173 58% 39%))',
  'hsl(var(--chart-3, 197 37% 24%))',
  'hsl(var(--chart-4, 43 74% 66%))',
  'hsl(var(--chart-5, 27 87% 67%))'
];

export interface ChartMixedOccupancyWAverageProps {
  labels: string[];
  series: { name: string; data: number[]; type?: 'bar' | 'line'; listingId?: string }[];
  percentageMode?: boolean;
}

export default function ChartMixedOccupancyWAverage({
  labels,
  series,
  percentageMode = true
}: ChartMixedOccupancyWAverageProps) {
  const [selectedSeries, setSelectedSeries] = useState<string | null>(null);

  const chartConfig: ChartConfig = {};
  series.forEach((s, i) => {
    chartConfig[s.name] = {
      label: s.name,
      color: CHART_COLORS[i % CHART_COLORS.length]
    };
  });

  const data = labels.map((label, i) => {
    const row: Record<string, string | number> = { month: label };
    series.forEach((s) => {
      row[s.name] = s.data[i] ?? 0;
    });
    return row;
  });

  const barSeries = series.filter((s) => s.type !== 'line');
  const lineSeries = series.filter((s) => s.type === 'line');

  const formatValue = (value: number) =>
    percentageMode ? `${Number(value).toFixed(1)}%` : String(value);

  const handleSeriesSelect = (name: string) => {
    setSelectedSeries((prev) => (prev === name ? null : name));
  };

  const renderTooltip: TooltipProps<number, string>['content'] = ({ active, payload, label }) => {
    if (!active || !payload?.length || label == null) return null;
    const filteredPayload =
      selectedSeries === null ? payload : payload.filter((p) => (p.name ?? '') === selectedSeries);
    if (filteredPayload.length === 0) return null;
    return (
      <div className="rounded-md border bg-popover px-3 py-2 text-sm shadow-md">
        <div className="mb-1 font-medium">{label}</div>
        {filteredPayload.map((entry) => (
          <div
            key={entry.name ?? entry.dataKey}
            className="flex items-center justify-between gap-4"
          >
            <span style={{ color: entry.color }}>{entry.name ?? ''}</span>
            <span>{formatValue(Number(entry.value ?? 0))}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderLegend = () => (
    <div className="flex flex-wrap justify-center gap-4 pt-2">
      {series.map((s) => {
        const isSelected = selectedSeries === null || selectedSeries === s.name;
        const color =
          s.type === 'line'
            ? CHART_COLORS[(barSeries.length + lineSeries.indexOf(s)) % CHART_COLORS.length]
            : CHART_COLORS[barSeries.indexOf(s) % CHART_COLORS.length];
        return (
          <button
            key={s.name}
            type="button"
            className="flex items-center gap-1.5 text-sm transition-opacity hover:opacity-80 disabled:cursor-default disabled:opacity-50"
            style={{ opacity: isSelected ? 1 : 0.5 }}
            onClick={() => handleSeriesSelect(s.name)}
            onKeyDown={(e) => e.key === 'Enter' && handleSeriesSelect(s.name)}
          >
            {s.type === 'line' ? (
              <span className="h-0.5 w-4 shrink-0" style={{ backgroundColor: color }} />
            ) : (
              <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: color }} />
            )}
            <span>{s.name}</span>
          </button>
        );
      })}
    </div>
  );

  const focusRingRemoval =
    '[&_.recharts-responsive-container]:outline-none [&_.recharts-responsive-container]:focus:outline-none [&_.recharts-responsive-container]:ring-0 [&_.recharts-wrapper]:outline-none [&_.recharts-wrapper]:focus:outline-none [&_.recharts-wrapper]:ring-0 [&_.recharts-bar-rectangle]:outline-none [&_.recharts-bar-rectangle]:focus:outline-none [&_.recharts-bar-rectangle]:ring-0';

  return (
    <div className="flex w-full min-h-[364px] flex-col overflow-visible">
      <ChartContainer config={chartConfig} className={`h-[364px] w-full ${focusRingRemoval}`}>
        <ComposedChart
          data={data}
          margin={{ top: 8, right: 0, bottom: 8, left: 0 }}
          barCategoryGap="20%"
        >
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis dataKey="month" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
          <YAxis
            tick={{ fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => (percentageMode ? `${v}%` : String(v))}
            domain={percentageMode ? [0, 100] : undefined}
          />
          <Tooltip
            content={renderTooltip}
            contentStyle={{
              borderRadius: 8,
              border: '1px solid hsl(var(--border))'
            }}
          />
          {barSeries.map((s, i) => {
            const isSelected = selectedSeries === null || selectedSeries === s.name;
            return (
              <Bar
                key={s.name}
                dataKey={s.name}
                fill={CHART_COLORS[i % CHART_COLORS.length]}
                radius={4}
                opacity={isSelected ? 1 : 0.35}
                onClick={() => handleSeriesSelect(s.name)}
              />
            );
          })}
          {lineSeries.map((s, i) => {
            const isSelected = selectedSeries === null || selectedSeries === s.name;
            return (
              <Line
                key={s.name}
                type="monotone"
                dataKey={s.name}
                stroke={CHART_COLORS[(barSeries.length + i) % CHART_COLORS.length]}
                strokeWidth={2}
                strokeOpacity={isSelected ? 1 : 0.35}
                dot={false}
                onClick={() => handleSeriesSelect(s.name)}
              />
            );
          })}
        </ComposedChart>
      </ChartContainer>
      <div className="shrink-0">{renderLegend()}</div>
    </div>
  );
}
